<script setup lang="ts">
import { nextTick, ref } from 'vue';
import EntryMaterials from '@/core/dialogs/tabbed-detail-dialog/EntryMaterials.vue';
import EntryMaterialSelection from '@/core/dialogs/tabbed-detail-dialog/EntryMaterialSelection.vue';
import DetailRelationActionRow from '@/core/dialogs/tabbed-detail-dialog/DetailRelationActionRow.vue';
import type { Components } from '@/api/generated/api';
import { useSupportMeasureEntry } from '@/composables/supportMeasureEntry/useSupportMeasureEntry';
import EntryAnnotationField from '@/core/dialogs/tabbed-detail-dialog/EntryAnnotationField.vue';
import CompetenceSelection from '@/core/selects/CompetenceSelection.vue';
import StudentSelection from '@/core/selects/StudentSelection.vue';
import type { ISelectedCompetence, TStudentLike } from '@/interfaces';
import { SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH } from 'common/constants/support.measure.constants';

const props = defineProps<{
  allEligibleStudentsSelected: boolean;
  allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
  eligibleStudents: TStudentLike[];
  entry: Components.Schemas.SupportMeasureEntryDto;
  readonly?: boolean;
  selectedCompetences: ISelectedCompetence[];
  subjectDomains: Components.Schemas.CompetenceDomainDto[];
}>();

const emit = defineEmits<{
  'add:competence': [competenceId: number];
  'remove:competence': [competenceId: number];
  'select:all-students': [];
  'update:competences': [competenceIds: number[]];
  'update:students': [students: TStudentLike[]];
}>();

const { update, setMaterials } = useSupportMeasureEntry();
const materialsSaving = ref(false);

const onUpdateMaterials = async (ids: number[]): Promise<void> => {
  if (props.readonly || materialsSaving.value) {
    return;
  }
  materialsSaving.value = true;
  try {
    await setMaterials(props.entry.id, ids);
    await nextTick();
  } finally {
    materialsSaving.value = false;
  }
};

// Empty text clears the optional description.
const onCommitDescription = (value: string): void => {
  const description = value.trim();
  if (description === (props.entry.description ?? '')) return;
  void update(props.entry.id, { description: description || null });
};
</script>

<template>
  <div class="flex flex-col gap-md p-content max-md:p-lg">
    <StudentSelection
      class="max-w-full self-start"
      :all-eligible-students-selected="props.allEligibleStudentsSelected"
      commit-on-close
      :eligible-students="props.eligibleStudents"
      mobile-drawer
      :read-only="readonly"
      :selected-students="entry.students"
      @update:students="emit('update:students', $event)"
      @select:all-eligible-students="emit('select:all-students')"
    />

    <EntryAnnotationField
      :label="$t('supportMeasureEntry.notes')"
      :maxlength="SUPPORT_MEASURE_DESCRIPTION_MAX_LENGTH"
      name="support-measure-entry-notes"
      :readonly="readonly"
      :value="entry.description ?? ''"
      @commit="onCommitDescription"
    />

    <EntryMaterials
      :materials="entry.materials"
      :readonly="readonly"
      :disabled="materialsSaving"
      @update:materials="onUpdateMaterials"
    />

    <DetailRelationActionRow v-if="!readonly" :separated="!entry.competences.length">
      <EntryMaterialSelection
        :materials="entry.materials"
        :school-subject-id="entry.schoolSubjectId"
        :school-class-id="entry.schoolClassId"
        :disabled="materialsSaving"
        @update:materials="onUpdateMaterials"
      />
      <template v-if="!entry.competences.length" #secondary>
        <CompetenceSelection
          :all-school-subjects="props.allSchoolSubjects"
          :competence-domains="props.subjectDomains"
          commit-on-close
          inline-empty
          mobile-drawer
          :school-subject-id="entry.schoolSubjectId"
          :selected-competences="props.selectedCompetences"
          surface="secondary"
          @add:competence="emit('add:competence', $event)"
          @remove:competence="emit('remove:competence', $event)"
          @update:competences="emit('update:competences', $event)"
        />
      </template>
    </DetailRelationActionRow>

    <section v-if="entry.competences.length" class="flex flex-col gap-sm">
      <h2 class="flex min-h-button items-center py-sm text-body font-semibold leading-[1.7] text-secondary">
        {{ $t('performanceAssessmentEntry.detailDialog.linkedCompetences') }}
      </h2>
      <CompetenceSelection
        :all-school-subjects="props.allSchoolSubjects"
        :competence-domains="props.subjectDomains"
        commit-on-close
        mobile-drawer
        :read-only="readonly"
        :school-subject-id="entry.schoolSubjectId"
        :selected-competences="props.selectedCompetences"
        surface="secondary"
        @add:competence="emit('add:competence', $event)"
        @remove:competence="emit('remove:competence', $event)"
        @update:competences="emit('update:competences', $event)"
      />
    </section>
  </div>
</template>
